import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { baseurImage } from "../../../Baseurl";
import Swal from "sweetalert2";
import { hasSubModulePermission } from "../../../utils/permissions";
import axiosInstance from "../../../api/axiosInstance";

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#4680ff',
  red:    '#9E2B25',
  green:  '#2CA87F',
  orange: '#F29E4C',
}

const thStyle = {
  fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase',
  letterSpacing: '0.05em', padding: '8px 12px', background: '#f8f9fb',
  borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap',
}
const tdStyle = {
  fontSize: 12, color: '#2d3748', padding: '7px 12px',
  verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5',
}
const inputStyle = {
  height: 34, borderRadius: 8, border: '1px solid #dde3eb',
  padding: '0 10px', fontSize: 12, color: '#2d3748', outline: 'none', background: '#fff',
}
const paginBtn = (disabled) => ({
  width: 28, height: 28, borderRadius: 6, border: `1px solid ${disabled ? '#e8ecf0' : COLORS.blue}`,
  background: disabled ? '#f5f7fa' : COLORS.blue, color: disabled ? '#aaa' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center', fontSize: 13,
})

function ActionBtn({ icon, color, title, onClick, as: Tag = 'button', to, state }) {
  const style = {
    width: 28, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer',
    background: `${color}18`, color, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 14, textDecoration: 'none',
  }
  if (Tag === NavLink) {
    return (
      <NavLink to={to} state={state} title={title} style={style}
        onMouseEnter={e => e.currentTarget.style.background = color + '33'}
        onMouseLeave={e => e.currentTarget.style.background = color + '18'}
      ><i className={`ti ${icon}`} /></NavLink>
    )
  }
  return (
    <button onClick={onClick} title={title} style={style}
      onMouseEnter={e => e.currentTarget.style.background = color + '33'}
      onMouseLeave={e => e.currentTarget.style.background = color + '18'}
    ><i className={`ti ${icon}`} /></button>
  )
}

function Badge({ label, bg, color }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
      background: bg || '#e8ecf0', color: color || COLORS.gray, whiteSpace: 'nowrap',
    }}>{label}</span>
  )
}

export default function Patient() {
  const navigate = useNavigate();
  const [patients,    setPatients]    = useState([]);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const userRaw = localStorage.getItem('userMedical');
  const isAdmin = userRaw && JSON.parse(userRaw)?.data?.role === 'admin';

  useEffect(() => { getdata(); }, []);

  const getdata = async () => {
    try {
      let endpoint = 'getAllPatients';
      const userRaw = localStorage.getItem('userMedical');
      if (userRaw) {
        const user = JSON.parse(userRaw);
        if (user?.data?.role === 'doctor') {
          const modRes = await axiosInstance.get('getModuleSettings');
          const restrictedMod = modRes.data?.data?.find(m => m.module_name === 'restricted_files');
          if (restrictedMod?.enable === 1 && user.data.id) {
            endpoint = `getPatientsByDoctor/${user.data.id}`;
          }
        }
      }
      const response = await axiosInstance.get(endpoint);
      if (response.data.success) {
        setPatients(response.data.data.map(p => ({
          ...p, profileImage: decodeURIComponent(p.profileImage),
        })));
      }
    } catch (e) { console.error('Error fetching patients:', e.message); }
  };

  const handledelete = async (id) => {
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    const { value } = await Swal.fire({
      title: 'Confirm Deletion',
      icon: 'warning',
      html: `<p style="margin-bottom:10px;font-size:13px;color:#5B6B79">Type the code below to confirm deleting this patient record. This action cannot be undone.</p>
             <div style="font-size:28px;font-weight:700;letter-spacing:10px;color:#9E2B25;margin:10px 0 16px">${code}</div>`,
      input: 'text',
      inputPlaceholder: 'Enter 4-digit code',
      inputAttributes: { maxlength: 4, autocomplete: 'off', style: 'text-align:center;font-size:20px;letter-spacing:6px;font-weight:700' },
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#9E2B25',
      cancelButtonText: 'Cancel',
      preConfirm: (val) => {
        if (val !== code) {
          Swal.showValidationMessage('Incorrect code. Please try again.');
          return false;
        }
        return true;
      },
    });
    if (value) {
      try { await axiosInstance.delete(`deletePatient/${id}`); Swal.fire('Deleted!', 'Patient has been deleted.', 'success'); getdata(); }
      catch { Swal.fire('Error!', 'Something went wrong while deleting.', 'error'); }
    }
  };

  const filteredPatients = patients.filter(item =>
    `${item.firstName} ${item.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.fileNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.gender?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.Primary_Doctor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.mobileNumber?.toString().includes(searchTerm) ||
    item.age?.toString().includes(searchTerm) ||
    item.primaryDoctor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.civilIdNumber?.toString().includes(searchTerm)
  );

  const totalPages     = Math.ceil(filteredPatients.length / itemsPerPage) || 1;
  const indexOfFirst   = (currentPage - 1) * itemsPerPage;
  const currentPatients = filteredPatients.slice(indexOfFirst, indexOfFirst + itemsPerPage);

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, marginRight: 4, flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            ><i className="ti ti-arrow-left" /></button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-users" style={{ color: COLORS.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Patient List</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage and view all registered patients</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <button
                className="bgBtn btn-sm d-flex align-items-center gap-1"
                style={{ fontSize: 13, padding: '6px 14px' }}
                onClick={() => navigate('/Admin/addPatient')}
              >
                <i className="ti ti-plus" style={{ fontSize: 14 }} /> Patient Registration
              </button>
            </div>
          </div>

          {/* ── Filter Bar ── */}
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #e8ecf0', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <i className="ti ti-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: COLORS.gray, fontSize: 13, pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search by name, file no., mobile, CPR…"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                style={{ ...inputStyle, paddingLeft: 30, width: 280 }}
              />
            </div>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.gray }}>
              {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* ── Table ── */}
          <div style={{ padding: '14px 20px' }}>
            <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, borderLeft: `3px solid ${COLORS.blue}` }}>#</th>
                    <th style={thStyle}>Patient</th>
                    <th style={thStyle}>File No.</th>
                    <th style={thStyle}>Gender</th>
                    <th style={thStyle}>Age</th>
                    <th style={thStyle}>Mobile</th>
                    <th style={thStyle}>CPR No.</th>
                    <th style={thStyle}>Primary Doctor</th>
                    <th style={thStyle}>Revisits</th>
                    <th style={thStyle}>Last Visit</th>
                    <th style={thStyle}>Balance</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPatients.length === 0 ? (
                    <tr>
                      <td colSpan={12} style={{ ...tdStyle, textAlign: 'center', padding: '36px 0', color: COLORS.gray }}>
                        <i className="ti ti-users-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />
                        No patients found
                      </td>
                    </tr>
                  ) : currentPatients.map((item, i) => {
                    const rowBg = i % 2 === 0 ? '#fff' : '#fafbfc';
                    const lastVisit = item.lastVisitDate && !isNaN(new Date(item.lastVisitDate))
                      ? new Date(item.lastVisitDate).toLocaleDateString('en-GB') : '—';
                    return (
                      <tr key={item.id || i} style={{ background: rowBg }}
                        onMouseEnter={e => e.currentTarget.style.background = `${COLORS.blue}0d`}
                        onMouseLeave={e => e.currentTarget.style.background = rowBg}
                      >
                        <td style={{ ...tdStyle, color: COLORS.gray, fontSize: 11 }}>{indexOfFirst + i + 1}</td>

                        {/* Name + avatar */}
                        <td style={tdStyle}>
                          <NavLink
                            to="/Admin/Manageappointment"
                            state={{ patientid: item }}
                            style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            <div style={{
                              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                              backgroundImage: `url(${baseurImage}${item.profileImage})`,
                              backgroundSize: 'cover', backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat', border: '1.5px solid #e8ecf0',
                              background: `url(${baseurImage}${item.profileImage}) center/cover no-repeat, ${COLORS.blue}22`,
                            }} />
                            <span style={{ fontWeight: 600, fontSize: 12, color: '#1a202c' }}>
                              {item.firstName} {item.lastName}
                            </span>
                          </NavLink>
                        </td>

                        <td style={tdStyle}>
                          <code style={{ fontSize: 11, background: '#f4f4f8', padding: '1px 5px', borderRadius: 4, color: '#555' }}>
                            {item.fileNumber || '—'}
                          </code>
                        </td>
                        <td style={tdStyle}>
                          {item.gender
                            ? <Badge
                                label={item.gender}
                                bg={item.gender?.toLowerCase() === 'male' ? '#e8f4fd' : '#fce8f3'}
                                color={item.gender?.toLowerCase() === 'male' ? '#2980b9' : '#c0392b'}
                              />
                            : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={tdStyle}>{item.age ? `${item.age}y` : '—'}</td>
                        <td style={tdStyle}>{item.mobileNumber || '—'}</td>
                        <td style={tdStyle}>{item.civilIdNumber || '—'}</td>
                        <td style={tdStyle}>{item.Primary_Doctor || '—'}</td>
                        <td style={tdStyle}>
                          <Badge
                            label={item.totalAppointments || 0}
                            bg={`${COLORS.blue}18`}
                            color={COLORS.blue}
                          />
                        </td>
                        <td style={{ ...tdStyle, color: COLORS.gray }}>{lastVisit}</td>
                        <td style={tdStyle}>
                          {item.pending_balance > 0
                            ? <Badge label={`${item.pending_balance} BHD`} bg="#fff7ed" color="#c2410c" />
                            : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                            <ActionBtn as={NavLink} to="/Admin/Manageappointment" state={{ patientid: item }} icon="ti-eye"  color={COLORS.blue}  title="View" />
                            {hasSubModulePermission(2) && (
                              <>
                                <ActionBtn icon="ti-edit"  color={COLORS.green} title="Edit"   onClick={() => navigate('/Admin/EditPatient', { state: { patientid: item } })} />
                                {isAdmin && (
                                  <ActionBtn icon="ti-trash" color={COLORS.red}   title="Delete" onClick={() => handledelete(item.id)} />
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 12, gap: 6 }}>
                <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} style={paginBtn(currentPage <= 1)}>
                  <i className="ti ti-chevron-left" />
                </button>
                <span style={{ fontSize: 12, color: COLORS.gray, fontWeight: 600 }}>{currentPage} / {totalPages}</span>
                <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} style={paginBtn(currentPage >= totalPages)}>
                  <i className="ti ti-chevron-right" />
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
